// frontend/src/components/orcamento/GridProjecao.js
// Tabela editável em seções (Despesas, depois Receitas) numa rolagem só, com o
// cabeçalho de meses grudado no topo. Cada seção abre com uma linha de total;
// no fim, a linha Resultado (receitas − despesas) mês a mês, já com as edições.
// Selo, P50 preenchido, faixa P10·P90 no tooltip, célula vazia para
// vermelha/decisão (traço, não zero). Não sabe de rede: recebe tudo por props.
import React, { useEffect, useRef, useState } from 'react';
import { parseBR, ehSintetica, ehSomenteLeitura } from './useProjecao';
import { notificar } from '../../utils/notify';

const fmt = (v) => (v === null || v === undefined) ? '' :
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
const fmtK = (v) => (v === null || v === undefined) ? '—' :
  (Math.abs(v) >= 1000 ? `${(v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}k` : fmt(v));
// resultado: sinal explícito (− para prejuízo), não só a cor
const fmtSinal = (v) => (v < 0 ? `−${fmt(Math.abs(v))}` : fmt(v || 0));
const MESES3 = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const MOTIVO = {
  verde: 'Confiável: ≥ 24 meses de histórico e variação < 35 %. Pode adotar o P50.',
  amarelo: 'Razoável: histórico médio ou variação entre 35 % e 100 %. Confira antes de aceitar.',
  vermelho: 'Não previsível: pouco histórico ou variação ≥ 100 %. Digite o valor.',
  decisao: 'Decisão: política da empresa, não previsão. Digite o valor.',
  premissa: 'Vem das premissas (aba Premissas). Não é editável na grade.',
};

// marcador da categoria: bolinha do selo ou o selo "P" das premissas
export function MarcaSelo({ cat }) {
  if (cat.selo === 'premissa') return <span className="op-selo-p" title="Vem das premissas">P</span>;
  return <span className={`op-selo ${cat.selo}`}></span>;
}

// Célula com texto próprio enquanto está em edição: o valor só vira número
// (e sobe para o hook) ao sair do campo ou no Enter. Se formatasse a cada
// tecla, "1.234,56" seria reescrito no meio da digitação. Esc desfaz.
function Celula({ valor, editado, semProjecao, onCommit }) {
  const [txt, setTxt] = useState(fmt(valor));
  const [foco, setFoco] = useState(false);
  const cancelado = useRef(false);
  useEffect(() => { if (!foco) setTxt(fmt(valor)); }, [valor, foco]);
  const commit = () => {
    if (cancelado.current) { cancelado.current = false; setTxt(fmt(valor)); return; }
    if (txt === fmt(valor)) return;                     // nada mudou (não marca como editado)
    if (txt.trim() === '') { onCommit(null); return; }  // célula limpa de propósito
    const n = parseBR(txt);
    if (n === null || n < 0) { setTxt(fmt(valor)); notificar('Valor inválido'); return; }
    if (n !== valor) onCommit(n);
    setTxt(fmt(n));
  };
  return (
    <input
      className={`op-input ${editado ? 'editado' : ''} ${(valor === null || valor === undefined) ? 'vazio' : ''}`}
      value={txt}
      placeholder={semProjecao ? '—' : ''}
      onChange={(e) => setTxt(e.target.value)}
      onFocus={(e) => { setFoco(true); e.target.select(); }}
      onBlur={() => { setFoco(false); commit(); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.target.blur();
        if (e.key === 'Escape') { cancelado.current = true; e.target.blur(); }
      }}
    />
  );
}

const fmtR = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v || 0);

// Selos das empresas de origem (visão consolidada): "ADV" / "AWG" ou o id;
// o tooltip lista o P50 do ano de cada origem. `siglas` = {cliente_id: {sigla, nome}}.
export function Empresas({ origens, siglas }) {
  if (!origens || !origens.length || !siglas) return null;
  const dica = origens.map((o) => `${siglas[o.cliente_id]?.nome || o.cliente_id}: P50 ${fmtR(o.total_p50)} no ano`).join('\n');
  return (
    <span className="op-emps" title={dica}>
      {origens.map((o) => (
        <span key={`${o.cliente_id}-${o.codigo}`} className="op-emp">{siglas[o.cliente_id]?.sigla || o.cliente_id}</span>
      ))}
    </span>
  );
}

// linhas de UMA seção: cabeçalho com o total e as categorias editáveis
function Secao({ natureza, titulo, categorias, meses, totais, valorCelula, edicoes, onEditar, onAbrirCategoria,
  somenteLeitura, siglas }) {
  const ed = edicoes[natureza] || {};
  return (
    <>
      <tr className={`op-secao ${natureza}`}>
        <td>{titulo} <span className="op-secao-n">{categorias.length} categorias</span></td>
        {meses.map((m) => <td key={m} className="op-secao-v">{fmt(totais.porMes[m] || 0)}</td>)}
        <td className="op-ano">{fmt(totais.ano)}</td>
      </tr>
      {!categorias.length && (
        <tr><td colSpan={meses.length + 2} className="text-muted small">Sem títulos no histórico para projetar.</td></tr>
      )}
      {categorias.map((c) => {
        const totAno = meses.reduce((s, m) => s + (valorCelula(natureza, c, m) || 0), 0);
        const semProjecao = c.selo === 'vermelho' || c.selo === 'decisao';
        const sintetica = ehSintetica(c);
        const leitura = somenteLeitura || ehSomenteLeitura(c);
        const consolidada = Array.isArray(c.origens);
        const motivo = consolidada ? 'Clique para abrir na empresa de origem.'
          : (c.valor_fixo ? 'Valor fixo nas premissas (aba Premissas).' : (MOTIVO[c.selo] || ''));
        const dicaRo = somenteLeitura ? 'Visão consolidada: somente leitura'
          : (c.valor_fixo ? 'Valor fixo nas premissas' : 'Vem das premissas');
        const abrir = () => onAbrirCategoria(natureza, c);
        // sintética não tem histórico para analisar: o nome não abre o painel
        const acao = sintetica ? {} : {
          role: 'button', tabIndex: 0, onClick: abrir,
          onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); abrir(); } },
        };
        return (
          <tr key={`${natureza}-${c.codigo}`} className={leitura ? 'op-linha-ro' : ''}>
            <td className={`op-cat ${sintetica ? 'sint' : ''}`} title={`${sintetica || consolidada ? '' : `${c.codigo} `}${c.nome}
${motivo}`} {...acao}>
              <MarcaSelo cat={c} />
              {!sintetica && !consolidada && <span className="cod">{c.codigo}</span>}{c.nome}
              <Empresas origens={c.origens} siglas={siglas} />
            </td>
            {meses.map((m) => {
              const pm = (c.meses || []).find((x) => x.mes === m) || {};
              const v = valorCelula(natureza, c, m);
              const editado = !!(ed[c.codigo] && Object.prototype.hasOwnProperty.call(ed[c.codigo], m));
              return (
                // faixa P10–P90 fica no tooltip: dentro da célula poluía a leitura
                <td key={m} className="op-cell"
                  title={(pm.p10 !== null && pm.p10 !== undefined) ? `Faixa provável: ${fmtK(pm.p10)} a ${fmtK(pm.p90)}` : undefined}>
                  {leitura
                    ? <span className="op-ro" title={dicaRo}>{fmt(v) || '—'}</span>
                    : <Celula valor={v} editado={editado} semProjecao={semProjecao}
                        onCommit={(n) => onEditar(natureza, c.codigo, m, n)} />}
                </td>
              );
            })}
            <td className="op-ano">{fmt(totAno)}</td>
          </tr>
        );
      })}
    </>
  );
}

export default function GridProjecao({ secoes, meses, totais, valorCelula, edicoes, onEditar, onAbrirCategoria,
  somenteLeitura = false, siglas = null }) {
  const res = totais.resultado;
  return (
    // ocupa a altura da tela (descontando cabeçalho, abas e rodapé de pilares)
    <div className="pa-card-body-table" style={{ maxHeight: 'calc(100vh - 80px)', minHeight: 600, overflow: 'auto' }}>
      <table className="op-table">
        <thead>
          <tr>
            <th style={{ textAlign: 'left', minWidth: 280 }}>Categoria</th>
            {meses.map((m) => <th key={m} style={{ textAlign: 'right' }}>{MESES3[parseInt(m.slice(5, 7), 10) - 1]}</th>)}
            <th style={{ textAlign: 'right' }}>Ano</th>
          </tr>
        </thead>
        <tbody>
          {secoes.map((s) => (
            <Secao key={s.natureza} natureza={s.natureza} titulo={s.titulo} categorias={s.categorias}
              meses={meses} totais={totais[s.natureza]} valorCelula={valorCelula} edicoes={edicoes}
              onEditar={onEditar} onAbrirCategoria={onAbrirCategoria} somenteLeitura={somenteLeitura} siglas={siglas} />
          ))}
          <tr className="op-resultado">
            <td>Resultado <span className="op-secao-n">receitas − despesas</span></td>
            {meses.map((m) => {
              const v = res.porMes[m] || 0;
              return (
                <td key={m} className={v < 0 ? 'neg' : ''} title={v < 0 ? 'Prejuízo no mês' : 'Lucro no mês'}>
                  {fmtSinal(v)}
                </td>
              );
            })}
            <td className={`op-ano ${res.ano < 0 ? 'neg' : ''}`}>
              {fmtSinal(res.ano)}
              <span className="op-res-tag">{res.ano < 0 ? 'prejuízo' : 'lucro'}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
